import { fmtInt, fmtPercent } from '../lib/format.ts';

type Tone = 'ok' | 'warn' | 'crit' | 'info';

interface ProgressBarProps {
  value: number;
  target: number;
  label: string;
  tone?: Tone;
  showFooter?: boolean;
  hint?: string;
  currency?: boolean;
}

const toneText: Record<Tone, string> = {
  ok: 'text-[--color-ok]',
  warn: 'text-[--color-warn]',
  crit: 'text-[--color-crit]',
  info: 'text-[--color-info]',
};

const toneGlow: Record<Tone, string> = {
  ok: 'card-glow-ok',
  warn: 'card-glow-warn',
  crit: 'card-glow-crit',
  info: '',
};

const toneStrip: Record<Tone, string> = {
  ok: 'tone-strip-ok',
  warn: 'tone-strip-warn',
  crit: 'tone-strip-crit',
  info: 'tone-strip-info',
};

const toneFillFrom: Record<Tone, string> = {
  ok: 'from-[--color-ok]/70 to-[--color-ok]',
  warn: 'from-[--color-warn]/70 to-[--color-warn]',
  crit: 'from-[--color-crit]/70 to-[--color-crit]',
  info: 'from-[--color-info]/70 to-[--color-info]',
};

export function ProgressBar({ value, target, label, tone, showFooter = true, hint, currency }: ProgressBarProps) {
  const safeTarget = target > 0 ? target : 1;
  const pct = (value / safeTarget) * 100;
  const clamped = Math.min(100, Math.max(0, pct));
  const auto: Tone = pct >= 90 ? 'ok' : pct >= 60 ? 'warn' : 'crit';
  const t = tone ?? auto;

  const fmt = (n: number) =>
    currency
      ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
      : fmtInt(n);

  return (
    <div className={`card-base p-6 ${toneGlow[t]}`}>
      <span className={`tone-strip ${toneStrip[t]}`} aria-hidden />
      <div className="relative flex items-baseline justify-between gap-3">
        <h3 className="eyebrow">{label}</h3>
        {hint && <span className="text-[11px] text-[--color-fg-muted]">{hint}</span>}
      </div>

      <div className="relative mt-4 flex items-end gap-3 tabular-nums">
        <span className={`text-7xl font-bold leading-none tracking-tight ${toneText[t]}`}>{fmt(value)}</span>
        <span className="pb-2 text-base text-[--color-fg-dim]">/ {fmt(target)}</span>
        <span className={`ml-auto pb-2 text-3xl font-bold tabular-nums tracking-tight ${toneText[t]}`}>
          {fmtPercent(pct, 0)}
        </span>
      </div>

      <div className="relative mt-5 h-2.5 overflow-hidden rounded-full bg-[--color-card-hi]">
        <div
          className={`h-full rounded-full bg-gradient-to-r transition-[width] duration-700 ease-out ${toneFillFrom[t]}`}
          style={{ width: `${clamped}%` }}
        />
        {pct >= 100 && (
          <div className="absolute inset-y-0 right-0 w-1 bg-white/40" />
        )}
      </div>

      {showFooter && (
        <div className="relative mt-3 flex justify-between text-[10px] uppercase tracking-wider text-[--color-fg-muted]">
          <span>realizado</span>
          <span>
            {pct >= 100 ? `+${fmtInt(value - target)} acima da meta` : `faltam ${fmt(Math.max(0, target - value))}`}
          </span>
        </div>
      )}
    </div>
  );
}
